import { useTranslator } from "@eo-locale/react";

import { orderBy, sortBy } from "lodash";

import { useRouter } from "next/router";

import { memo, useContext, useEffect } from "react";

import {
  clearAllErrors,
  resetAnswers
} from "@/redux/features/place-form/slice";
import { setView } from "@/redux/features/place-right-panel/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Text,
  useMediaQuery
} from "@/storybook-components";

import { EsriContext } from "../../context/esri-context";
import { EsriContextType } from "../../context/esri-context-type";
import CheckboxQuestionComponent from "../../survey/questions/checkbox-question";
import DropdownQuestionComponent from "../../survey/questions/dropdown-question";
import EssayQuestion from "../../survey/questions/essay-question";
import ImageQuestion from "../../survey/questions/image-question";
import RadioButtonQuestionComponent from "../../survey/questions/radiobutton-question";
import SingleLineQuestionComponent from "../../survey/questions/singleline-question";
import SelectPins from "./select-pins";

import {
  resetMarker,
  resetMarkerError,
  setCurrentPageNumber,
  setPreviousMarker
} from "@/redux/features/place-marker/slice";
import { setPinDetails } from "@/redux/features/place/slice";

import { resetFilterCategories } from "@/redux/features/place-markers/slice";

import usePinFlow from "@/hooks/places/use-pin-flow";

const CreateAndEditPin = ({ symbols, handleCloseDrawer }) => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { translate } = useTranslator();
  const centerCoordinates = useAppSelector(
    (state) => state.place.centerCoordinates
  );
  const removeCreateFromUrl = () => {
    if (window?.location?.href?.includes("#create-pin")) {
      const newurl = window?.location?.href?.split("#")[0];
      router.replace(newurl, undefined, { shallow: true });
    }
  };
  const { setInteractionsVisibility, mapRef } = useContext(
    EsriContext
  ) as EsriContextType;
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  const { isEditMode, marker: editableMarker } = useAppSelector(
    (state) => state.placesMarkerSlice
  );
  const { marker } = useAppSelector((state) => state.placesMarkerSlice);

  const view = useAppSelector((state) => state.placesRightPanelView.view);

  const navbarElement = document.getElementsByClassName(
    "ehq-participant-header"
  )[0];
  const { login } = useAppSelector(
    (state) => state.user.registeredUser.profile
  );
  const calculatedHeightForDesktop = navbarElement ? "275px" : "140px";
  const saving = useAppSelector((state) => state.placesForm.saving);

  const sortedSymbols = orderBy(symbols, ["id"], ["asc"]);

  const { imageVisibility } = useAppSelector((state) => state.place.place);
  const isAddressVisible = useAppSelector(
    (state) => state.place.place.showAddress
  );
  const questions = useAppSelector((state) => state.placesForm.questions);

  const markerInfo: any = useAppSelector(
    (state) => state.placesMarkerSlice.marker
  );

  const { createMarker, editMarker } = usePinFlow();

  const handleCancelFlow = async () => {
    await dispatch(resetFilterCategories());
    await dispatch(resetAnswers());
    await dispatch(clearAllErrors());
    await dispatch(resetMarkerError());
    await dispatch(setView("activity-feed"));
    await dispatch(resetMarker());
    await dispatch(setPinDetails({}));

    removeCreateFromUrl();

    if (centerCoordinates?.lat) {
      mapRef?.setView(
        [centerCoordinates?.lat, centerCoordinates?.lng],
        centerCoordinates?.zoom
      );
    }

    // In the mobile view, clicking on "Cancel" should close the drawer and display the widgets.
    if (isMobile) {
      handleCloseDrawer();
      setInteractionsVisibility(true);
    }
  };

  const handleClick = () => {
    if (isEditMode) {
      editMarker(marker);
      return;
    }
    removeCreateFromUrl();

    createMarker();
  };

  const renderQuestions = () => {
    return sortBy(questions, (o) => o.sequence)?.map((question, index) => {
      switch (question.type) {
        case "EssayQuestion":
          return <EssayQuestion question={question} key={index} />;
        case "SingleLineQuestion":
          return (
            <SingleLineQuestionComponent question={question} key={index} />
          );
        case "RadioButtonQuestion":
          return (
            <RadioButtonQuestionComponent question={question} key={index} />
          );
        case "DropDownQuestion":
          return (
            <DropdownQuestionComponent
              question={question}
              key={index}
              isClearable={isEditMode ? false : true}
            />
          );
        case "FileQuestion":
          return imageVisibility ? (
            <ImageQuestion question={question} key={index} />
          ) : null;
        case "CheckBoxQuestion":
          return <CheckboxQuestionComponent question={question} key={index} />;
        default:
          break;
      }
    });
  };

  const isPinInsidePolygon = useAppSelector(
    (state) => state.placesMarkerSlice.isMarkerInsidePolygon
  );
  const heightOfMap = login ? "135px" : "127px";
  const height = isMobile
    ? `calc(100svh - ${heightOfMap})`
    : `calc(100svh - ${calculatedHeightForDesktop})`;

  const renderPinInsidePolygonText = () => {
    return (
      <Box
        height={"75svh"}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {translate("places.pinOutsideBoundary")}
      </Box>
    );
  };

  const renderCreatePinBody = () => {
    const renderAddress = () => (
      <Text fontSize="xs" pt={6} color="blue.600">
        {isAddressVisible &&
          (isEditMode ? editableMarker?.address : markerInfo?.address)}
      </Text>
    );

    const renderRequiredField = () => (
      <Box textAlign="left" pt={6} pb={6}>
        <Text fontSize="xs">{translate("places.pin.requiredField")}</Text>
      </Box>
    );

    const Footer = (
      <Flex
        py={6}
        alignSelf="stretch"
        justifyContent={{ base: "space-between", lg: "flex-end" }}
        w="full"
        gap={6}
      >
        <Button
          w={{ base: 160, lg: 120 }}
          size={{ base: "lg", xl: "md" }}
          maxW={160}
          px={4}
          py={2}
          variant="secondary"
          width="full"
          onClick={handleCancelFlow}
        >
          {translate("common.cancel")}
        </Button>
        <Button
          w={{ base: 160, lg: 120 }}
          size={{ base: "lg", xl: "md" }}
          maxW={160}
          px={4}
          py={2}
          variant="primary"
          width="full"
          onClick={handleClick}
          isLoading={saving}
        >
          {view === "edit-pin"
            ? translate("common.update")
            : translate("places.pin.post")}
        </Button>
      </Flex>
    );

    return (
      <>
        <Box height={height} overflowY="auto">
          <Box px={4}>
            {renderAddress()}
            {renderRequiredField()}
            <Box>
              <FormControl isRequired>
                <FormLabel>{translate("places.pin.question")}</FormLabel>
                <SelectPins symbols={sortedSymbols} />
              </FormControl>
              {renderQuestions()}
            </Box>
          </Box>
        </Box>
        <Box px={4}>{Footer}</Box>
      </>
    );
  };
  useEffect(() => {
    dispatch(setPreviousMarker([]));
    dispatch(setCurrentPageNumber(1));
  }, []);

  return (
    <Box py={6} overflowY="auto" backgroundColor={"gray.100"} id="create-pin">
      <Heading size="md" px={4}>
        {view === "edit-pin"
          ? translate("places.pin.updatePin")
          : translate("places.pin.heading")}
      </Heading>

      {isPinInsidePolygon || isMobile
        ? renderCreatePinBody()
        : renderPinInsidePolygonText()}
    </Box>
  );
};

export default memo(CreateAndEditPin);
